import { useEffect, useRef } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Icon from "@amzn/meridian/esm/components/icon";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import errorTokens from "@amzn/meridian-tokens/base/icon/error";
import aimsTheme from "../../../styles/meridian-theme-aims.json";
import InMeetingExpFeedback from "../InMeetingExpFeedback";
import MentionCard from "../MentionCard";
import WhosWhoPanel from "../../whosWho/WhosWhoPanel";
import { THEME_COLOR } from "../../../styles/colors";
import { PROMPT_TYPE } from "../assistant.constants";
import { WHOS_WHO_STATE } from "../../whosWho/whosWho.constants";
import SectionCard from "../SectionCard";
import { parseStreamingText } from "./streamingCardParser";

const CARD_RENDERERS = {
  mention: (card, key) => (
    <MentionCard key={key} name={card.data.name || ""} time={card.data.time || ""} context={card.data.context || ""} detail={card.data.detail || ""} />
  ),
  section: (card, key) => (
    <SectionCard key={key} type={card.data.type || "topics"} title={card.data.title || ""} content={card.data.content || []} />
  ),
};

const renderBoldText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};


const ServerErrorMessage = () => (
  <Column spacing="300" spacingInset="300 0">
    <Icon tokens={errorTokens} color="error" />
    <Text type="b200">
      <strong>We hit a snag on what's happened so far.</strong>
    </Text>
    <Text type="b200">
      You can use other features or refresh the app.
    </Text>
  </Column>
);

const FreeformErrorMessage = () => (
  <Column spacing="300" spacingInset="300 0">
    <Text type="b200">
      {"😵 Our server is taking an unexpected break!"}
    </Text>
    <Text type="b200">
      {"Mind trying again in a few minutes? I'll be right here to help."}
    </Text>
  </Column>
);

const AgentMessage = ({ text, isError, promptType }) => {
  if (isError) {
    return promptType === PROMPT_TYPE.FREEFORM ? <FreeformErrorMessage /> : <ServerErrorMessage />;
  }

  const { plainText, completedCards, streamingCard } = parseStreamingText(text);

  return (
    <Column spacing="200" spacingInset="300 0">
      {plainText && plainText.split("\n").map((line, li) => {
        const trimmed = line.trim();
        if (!trimmed) return <Row key={li} height="8px" />;
        return (
          <Text key={li} type="b200" breakWord>
            {renderBoldText(trimmed)}
          </Text>
        );
      })}
      {completedCards.length > 0 && (
        <Column spacing="400" spacingInset="200 0 0 0">
          {completedCards.map((card, i) => {
            const renderer = CARD_RENDERERS[card.cardType];
            return renderer ? renderer(card, `card-${i}`) : null;
          })}
        </Column>
      )}
      {streamingCard && (
        <Column spacing="400" spacingInset="200 0 0 0">
          {CARD_RENDERERS[streamingCard.cardType]?.(streamingCard, "streaming")}
        </Column>
      )}
    </Column>
  );
};

const ChatMessages = ({ messages, isLoading, sessionId, aimsUuid, meetingId, meetingUuid, onFeedbackMessage, onOpenFeedbackPopover }) => {
  const isWhosWhoPending = messages.some((msg) => msg.whosWho && msg.isLoading);

  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  return (
    <Column
      spacing="none"
      width="318px"
      overflowY="auto"
      spacingInset="400 0"
    >
      {messages.map((msg, idx) => {
        if (!msg.text && !msg.isError && !msg.whosWho) return null;
        const prevMsg = messages[idx - 1];
        return (
          <Column key={`chat-${idx}`} spacing="none">
            {msg.role === "user" ? (
              <Row alignmentHorizontal="end" spacing="none">
                <Theme tokens={{ boxBorderRadiusFill: 8, boxBorderWidth: 0 }}>
                  <Box
                    type="fill"
                    backgroundColor={THEME_COLOR.MINT_GREEN}
                    spacingInset="350 400 350 400"
                    maxWidth="250px"
                  >
                    <Text type="b200" breakWord>{msg.text}</Text>
                  </Box>
                </Theme>
              </Row>
            ) : msg.whosWho ? (
              <WhosWhoPanel
                state={msg.isLoading ? WHOS_WHO_STATE.LOADING : msg.isError ? WHOS_WHO_STATE.ERROR : WHOS_WHO_STATE.LOADED}
                invitees={msg.invitees || []}
              />
            ) : (
              <>
                <AgentMessage text={msg.text || ""} isError={msg.isError} promptType={prevMsg?.promptType} />
                {msg.timestamp && msg.text !== "(No response)" && (
                  <InMeetingExpFeedback
                    sessionId={sessionId}
                    aimsUuid={aimsUuid}
                    messageTimestamp={msg.timestamp}
                    promptType={prevMsg?.promptType || PROMPT_TYPE.FREEFORM}
                    userInput={prevMsg?.text || ""}
                    agentResponse={msg.text}
                    meetingId={meetingId}
                    meetingUuid={meetingUuid}
                    onFeedbackMessage={onFeedbackMessage}
                    onOpenFeedbackPopover={onOpenFeedbackPopover}
                    streamingDisabled={isLoading}
                  />
                )}
              </>
            )}
            <Row height="16px" />
          </Column>
        );
      })}

      {isLoading && !isWhosWhoPending && (
        <Row alignmentHorizontal="start" spacing="none">
          <Box spacingInset="300">
            <Theme palette={aimsTheme}>
              <Loader size="small" />
            </Theme>
          </Box>
        </Row>
      )}

      <Row ref={bottomRef} height="1px" />
    </Column>
  );
};

export default ChatMessages;
